/**
 * Unified audio pronunciation player.
 *
 * Prioritizes the fast native Web Speech API (speechSynthesis) to deliver
 * instant, zero-latency pronunciation (< 50ms) without blocking on remote
 * network requests or CDN timeouts (matching the fast behavior of newly created words).
 *
 * Gracefully falls back to remote audio URL if Web Speech API is unavailable
 * or encounters an error.
 */
export function playWordPronunciation(
  word: string,
  audioUrl?: string | null,
  onStart?: () => void,
  onEnd?: () => void,
): void {
  const cleanWord = word ? word.trim() : '';
  if (!cleanWord) {
    onEnd?.();
    return;
  }

  const playAudioFallback = () => {
    if (audioUrl && audioUrl.trim()) {
      try {
        let isDone = false;
        const done = () => {
          if (!isDone) {
            isDone = true;
            onEnd?.();
          }
        };

        const audio = new Audio(audioUrl.trim());

        // Guard against hung/dead remote CDN requests
        const timeoutId = setTimeout(() => {
          audio.src = '';
          done();
        }, 2500);

        audio.onplaying = () => onStart?.();
        audio.onended = () => {
          clearTimeout(timeoutId);
          done();
        };
        audio.onerror = () => {
          clearTimeout(timeoutId);
          done();
        };

        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            clearTimeout(timeoutId);
            done();
          });
        }
      } catch {
        onEnd?.();
      }
    } else {
      onEnd?.();
    }
  };

  // Primary: Native browser SpeechSynthesis (instant, 0 network latency, 0 CDN dependency)
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(cleanWord);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      utterance.onstart = () => onStart?.();
      utterance.onend = () => onEnd?.();
      utterance.onerror = () => {
        // If native speech fails, try audio fallback
        playAudioFallback();
      };
      window.speechSynthesis.speak(utterance);
      return;
    } catch {
      playAudioFallback();
      return;
    }
  }

  // Fallback if speechSynthesis is not supported in the environment
  playAudioFallback();
}
